import { useEffect, useRef } from "react";

const KONAMI: readonly string[] = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a",
];

/**
 * Window-level keydown listener that buffers the last N keys and fires
 * `onMatch` when the buffer equals the Konami code (↑↑↓↓←→←→BA).
 *
 * Single-char keys are lowercased so Caps Lock doesn't break the match.
 * Modifier keys (alt/ctrl/meta) reset the buffer so chord shortcuts
 * (Cmd+ArrowUp etc.) don't accidentally seed the sequence.
 */
export function useKonamiCode(onMatch: () => void): void {
  const bufferRef = useRef<string[]>([]);
  const onMatchRef = useRef(onMatch);
  onMatchRef.current = onMatch;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) {
        bufferRef.current = [];
        return;
      }
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const next = [...bufferRef.current, key].slice(-KONAMI.length);
      bufferRef.current = next;
      if (
        next.length === KONAMI.length &&
        next.every((k, i) => k === KONAMI[i])
      ) {
        bufferRef.current = [];
        onMatchRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
